import BaseAPIService from './baseAPI';

// Website Analysis Types - Enhanced for MCP Backend
export interface AnalyzeWebsiteRequest {
  url: string;
  max_pages: number;
}

export interface BrandIdentity {
  brand_voice_and_tone: string;
  key_messaging_themes: string[];
  value_propositions: string[];
  brand_personality_traits: string[];
}

export interface TargetAudience {
  primary_audience_segments: string[];
  customer_pain_points_addressed: string[];
  industry_focus: string;
}

export interface ContentStrategy {
  content_themes_and_topics: string[];
  content_formats_used: string[];
  engagement_approaches: string[];
}

export interface CompetitivePositioning {
  unique_selling_points: string[];
  market_positioning: string;
  differentiation_factors: string[];
}

export interface BusinessFocus {
  core_products_services: string[];
  industry_verticals: string[];
  business_model_insights: string;
}

export interface ContentOpportunities {
  potential_linkedin_post_topics: string[];
  content_gaps_to_address: string[];
  trending_themes_to_leverage: string[];
}

export interface EnhancedWebsiteInsights {
  brand_identity: BrandIdentity;
  target_audience: TargetAudience;
  content_strategy: ContentStrategy;
  competitive_positioning: CompetitivePositioning;
  business_focus: BusinessFocus;
  content_opportunities: ContentOpportunities;
  metadata?: {
    analyzed_at: string;
    pages_count: number;
    base_url: string;
    page_summaries: any[];
  };
}

export interface WebsiteAnalysisResponse {
  success: boolean;
  url: string;
  insights: EnhancedWebsiteInsights;
  pages_analyzed?: number;
  error?: string;
}

export interface GenerateFromWebsiteRequest {
  url: string;
  content_type: 'post' | 'article';
  max_pages?: number;
}

export interface GenerateFromWebsiteResponse {
  success: boolean;
  url: string;
  content: string;
  content_type: string;
  hashtags: string[];
  image_suggestions: string[];
  pages_analyzed: number;
  generated_at: string;
  error?: string;
}

export interface GenerateFromInsightsRequest {
  insights: EnhancedWebsiteInsights;
  content_type: 'post' | 'article';
}

export interface GenerateFromInsightsResponse {
  success: boolean;
  content: string;
  content_type: string;
  hashtags: string[];
  image_suggestions: string[];
  error?: string;
}

export interface ExtractContentRequest {
  url: string;
}

export interface ExtractContentResponse {
  success: boolean;
  url: string;
  title: string;
  content: string;
  authors: string[];
  publish_date: string | null;
  top_image: string;
  keywords: string[];
  summary: string;
  extracted_by: string;
  error?: string;
}

export interface CompanyInfoRequest {
  content: string;
  url: string;
}

export interface CompanyInfo {
  company_name: string;
  industry: string;
  business_model: string;
  target_audience: {
    primary_segments: string[];
    demographics: string;
    geographic_focus: string;
  };
  products_services: {
    core_offerings: string[];
    key_features: string[];
    pricing_model: string;
  };
  brand_voice: {
    tone: string;
    messaging_themes: string[];
    value_propositions: string[];
  };
  content_themes: string[];
  technology_stack: string[];
  competitive_advantages: string[];
  content_opportunities: string[];
}

export interface CompanyInfoResponse {
  success: boolean;
  company_info: CompanyInfo;
  error?: string;
}

class WebsiteAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  async analyzeWebsite(request: AnalyzeWebsiteRequest): Promise<WebsiteAnalysisResponse> {
    try {
      const response = await this.post<WebsiteAnalysisResponse>('/website/analyze-website', request);
      return response;
    } catch (error) {
      console.error('Error analyzing website:', error);
      throw error;
    }
  }

  async generateFromWebsite(request: GenerateFromWebsiteRequest): Promise<GenerateFromWebsiteResponse> {
    try {
      const response = await this.post<GenerateFromWebsiteResponse>('/website/generate-from-url', request);
      return response;
    } catch (error) {
      console.error('Error generating content from website:', error);
      throw error;
    }
  }

  async generateFromInsights(request: GenerateFromInsightsRequest): Promise<GenerateFromInsightsResponse> {
    try {
      const response = await this.post<GenerateFromInsightsResponse>('/website/generate-from-insights', request);
      return response;
    } catch (error) {
      console.error('Error generating content from insights:', error);
      throw error;
    }
  }

  async extractContent(request: ExtractContentRequest): Promise<ExtractContentResponse> {
    try {
      const response = await this.post<ExtractContentResponse>('/website/extract-content', { url: request.url });
      return response;
    } catch (error) {
      console.error('Error extracting website content:', error);
      throw error;
    }
  }

  async extractCompanyInfo(request: CompanyInfoRequest): Promise<CompanyInfoResponse> {
    try {
      const response = await this.post<CompanyInfoResponse>('/website/extract-company-info', request);
      return response;
    } catch (error) {
      console.error('Error extracting company info:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async getWebsiteContent(url: string): Promise<{ data: { content: string; title: string } }> {
    try {
      const result = await this.extractContent({ url });
      return {
        data: {
          content: result.content,
          title: result.title
        }
      };
    } catch (error) {
      console.error('Error getting website content:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async analyzeCompetitor(url: string): Promise<WebsiteAnalysisResponse> {
    return this.analyzeWebsite({ url, max_pages: 3 });
  }
}

export const websiteAPI = new WebsiteAPIService();
export default websiteAPI; 